import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff } from 'lucide-react';
import api from '../utils/api';

function relativeTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function kindLabel(kind) {
    if (!kind) return 'Update';
    return kind.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function NotificationBell() {
    const nav = useNavigate();
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const rootRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (rootRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const refresh = async () => {
        try {
            const { data } = await api.get('/notifications', { params: { unread: true } });
            setCount(data.unreadCount || 0);
            setItems(data.items || []);
        } catch {}
    };

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 30_000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (open) refresh();
    }, [open]);

    const removeFromList = (id) => {
        setItems(prev => prev.filter(n => n._id !== id));
        setCount(c => Math.max(0, c - 1));
    };

    const markOne = async (n) => {
        try {
            await api.post(`/notifications/${n._id}/read`);
        } catch {
            /* still remove from UI if already read elsewhere */
        }
        removeFromList(n._id);
        setOpen(false);
        const link = (n.link || '').trim();
        if (link && link !== '#') nav(link);
    };

    const markAll = async () => {
        try {
            await api.post('/notifications/read-all');
            setItems([]);
            setCount(0);
        } catch {}
    };

    return (
        <div className="relative" ref={rootRef}>
            <button
                type="button"
                className="relative p-2 rounded-lg hover:bg-ink-100 transition-colors"
                onClick={() => setOpen(o => !o)}
                aria-label="Notifications"
                aria-expanded={open}
            >
                <Bell className="h-5 w-5" />
                {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white text-[10px] font-bold grid place-items-center ring-2 ring-white">
                        {count > 99 ? '99+' : count}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-[22rem] sm:w-96 max-h-[min(28rem,70vh)] flex flex-col overflow-hidden bg-white shadow-[0_16px_48px_rgba(15,23,42,0.14)] rounded-2xl z-30 border border-ink-100/80">
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-100 bg-ink-50/40">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="font-bold text-sm text-ink-900">Notifications</div>
                            {count > 0 && (
                                <span className="shrink-0 rounded-full bg-brand-teal/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-teal-700">
                                    {count} new
                                </span>
                            )}
                        </div>
                        {count > 0 && (
                            <button
                                type="button"
                                className="shrink-0 text-xs font-semibold text-brand-teal-700 hover:text-brand-teal-900 transition-colors"
                                onClick={markAll}
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div className="overflow-y-auto p-2">
                        {items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                                <div className="h-11 w-11 rounded-full bg-ink-100 grid place-items-center text-ink-400">
                                    <BellOff className="h-5 w-5" />
                                </div>
                                <div className="text-sm font-semibold text-ink-800">All caught up</div>
                                <div className="text-xs text-ink-500 max-w-[14rem]">
                                    New approvals and updates will show up here.
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {items.map(n => {
                                    const unread = !n.readAt;

                                    return (
                                        <button
                                            key={n._id}
                                            type="button"
                                            onClick={() => markOne(n)}
                                            className={`group w-full rounded-xl border border-ink-100/80 p-3 text-left transition-colors hover:bg-ink-50 ${unread ? 'bg-brand-teal-50/40' : 'bg-white'}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className={`text-sm leading-snug ${unread ? 'font-bold text-ink-900' : 'font-semibold text-ink-800'}`}>
                                                    {n.title}
                                                </div>
                                                {unread && (
                                                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-teal shadow-[0_0_0_3px_rgba(0,176,155,0.15)]" aria-hidden />
                                                )}
                                            </div>

                                            {n.body && (
                                                <p className="mt-1 text-xs leading-relaxed text-ink-600 line-clamp-2">
                                                    {n.body}
                                                </p>
                                            )}

                                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-ink-500">
                                                <span className="inline-flex items-center rounded-md bg-ink-100/80 px-1.5 py-0.5 font-medium text-ink-600">
                                                    {kindLabel(n.kind)}
                                                </span>
                                                <span>{relativeTime(n.createdAt)}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
